// =============================================
// Promo engine — issue & redeem coupons
// =============================================
// site_service_promos = the coupon definition. user_promo = the per-user ledger.
// Two redemption paths:
//   • PERSONAL code — pre-issued to one user (issuePersonalPromo). Only that user
//     can redeem it, once. The code carries a random suffix so it's non-guessable
//     and non-circulatable ("not shared over the internet").
//   • PUBLIC code — anyone types the promo's promo_code. Capped globally
//     (max_redemptions) and once per user.
// On redeeming a promo whose applies_to='service', a user_service_grant is created
// (source='promo', linked back via promo_id + granted_grant_id) — this is what
// makes the entitlement actually take effect (see serviceAccess.ts).
//
// All effects go through dataAccess (localStorage-or-API). Reads use the local
// cache (getTableData); callers should loadTable(...) afterward to refresh state.
// =============================================
import { getTableData, addRow, saveRow } from '../features/db-viewer/data/dataAccess';

export interface RedeemResult { ok: boolean; message: string; grantId?: number; promoId?: number; }

const todayStr = () => new Date().toISOString().slice(0, 10);
const nowStamp = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

/** Random, non-guessable suffix for personal codes. */
function randomSuffix(len = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous 0/O/1/I
  let out = '';
  for (let i = 0; i < len; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

function promoById(promoId: number): any | null {
  // eslint-disable-next-line eqeqeq
  return getTableData('site_service_promos').find((p: any) => p.promo_id == promoId && p.is_deleted != 1) || null;
}

function promoIsLive(p: any, today = todayStr()): { ok: boolean; reason: string } {
  if (!p || p.is_deleted == 1 || p.is_active == 0) return { ok: false, reason: 'This promo is inactive.' }; // eslint-disable-line eqeqeq
  if (p.start_date && String(p.start_date).slice(0, 10) > today) return { ok: false, reason: 'This promo has not started yet.' };
  if (p.end_date && String(p.end_date).slice(0, 10) < today) return { ok: false, reason: 'This promo has expired.' };
  return { ok: true, reason: '' };
}

/** Generate a unique personal code for a promo (base = promo_code). */
export function makePersonalCode(baseCode: string): string {
  const base = (baseCode || 'PROMO').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 16);
  const existing = new Set(getTableData('user_promo').map((r: any) => String(r.code).toUpperCase()));
  let code = `${base}-${randomSuffix()}`;
  let guard = 0;
  while (existing.has(code.toUpperCase()) && guard++ < 20) code = `${base}-${randomSuffix()}`;
  return code;
}

/** Issue a single-use personal code for `promoId` to `userId`. Returns the new code. */
export async function issuePersonalPromo(promoId: number, userId: number, createdBy?: number): Promise<{ ok: boolean; message: string; code?: string }> {
  const promo = promoById(promoId);
  if (!promo) return { ok: false, message: 'Promo not found.' };
  const code = makePersonalCode(promo.promo_code);
  try {
    await addRow('user_promo', {
      promo_id: promoId, code, issued_to_user_id: userId, status: 'issued',
      is_active: 1, is_deleted: 0, created_by: createdBy ?? null,
    }, 'user_promo_id');
    return { ok: true, message: `Issued personal code ${code}.`, code };
  } catch (e: any) {
    return { ok: false, message: e?.message || 'Could not issue code.' };
  }
}

/** Count successful redemptions of a promo. */
function redemptionCount(promoId: number): number {
  // eslint-disable-next-line eqeqeq
  return getTableData('user_promo').filter((r: any) => r.promo_id == promoId && r.status === 'redeemed' && r.is_deleted != 1).length;
}
function userAlreadyRedeemed(promoId: number, userId: number): boolean {
  // eslint-disable-next-line eqeqeq
  return getTableData('user_promo').some((r: any) => r.promo_id == promoId && r.redeemed_by_user_id == userId && r.status === 'redeemed' && r.is_deleted != 1);
}

// Apply a redeemed promo's effect: a 'service' promo grants the service to the user.
async function applyPromoEffect(promo: any, userId: number, createdBy?: number): Promise<number | undefined> {
  if (promo.applies_to !== 'service' || promo.ref_id == null) return undefined; // eslint-disable-line eqeqeq
  const serviceId = Number(promo.ref_id);
  // eslint-disable-next-line eqeqeq
  const existing = getTableData('user_service_grant').find((g: any) => g.user_id == userId && g.service_id == serviceId && g.is_deleted != 1);
  const payload = {
    access_level: 'full',
    valid_from: todayStr(),
    valid_until: promo.end_date ? String(promo.end_date).slice(0, 10) : null,
    source: 'promo', promo_id: promo.promo_id, status: 'active', is_active: 1, is_deleted: 0,
  };
  if (existing) {
    await saveRow('user_service_grant', existing.grant_id, payload, 'grant_id');
    return existing.grant_id;
  }
  const created = await addRow('user_service_grant', { user_id: userId, service_id: serviceId, ...payload, created_by: createdBy ?? null }, 'grant_id') as any;
  return created?.grant_id;
}

/**
 * Redeem a code for a user. Enforces personal-binding, single-use, per-user, global
 * cap, and the promo window. Creates a service grant when applicable.
 */
export async function redeemPromo(rawCode: string, userId: number, createdBy?: number): Promise<RedeemResult> {
  const code = (rawCode || '').trim();
  if (!code) return { ok: false, message: 'Enter a code.' };
  const upper = code.toUpperCase();

  // 1) Personal code? (pre-issued row bound to a user)
  const personal = getTableData('user_promo').find((r: any) =>
    // eslint-disable-next-line eqeqeq
    r.is_deleted != 1 && r.issued_to_user_id != null && String(r.code).toUpperCase() === upper);

  if (personal) {
    if (personal.status === 'redeemed') return { ok: false, message: 'This code has already been used.' };
    if (personal.status === 'revoked') return { ok: false, message: 'This code was revoked.' };
    // eslint-disable-next-line eqeqeq
    if (personal.issued_to_user_id != userId) return { ok: false, message: 'This code belongs to a different user.' };
    const promo = promoById(personal.promo_id);
    const live = promoIsLive(promo);
    if (!live.ok) return { ok: false, message: live.reason };
    const grantId = await applyPromoEffect(promo, userId, createdBy);
    await saveRow('user_promo', personal.user_promo_id, {
      status: 'redeemed', redeemed_by_user_id: userId, redeemed_at: nowStamp(), granted_grant_id: grantId ?? null,
    }, 'user_promo_id');
    await bumpRedemptions(promo);
    return { ok: true, message: `Redeemed ${personal.code}.`, grantId, promoId: promo.promo_id };
  }

  // 2) Public code? (matches a promo's promo_code)
  const promo = getTableData('site_service_promos').find((p: any) =>
    // eslint-disable-next-line eqeqeq
    p.is_deleted != 1 && String(p.promo_code).toUpperCase() === upper);
  if (!promo) return { ok: false, message: 'Invalid code.' };
  const live = promoIsLive(promo);
  if (!live.ok) return { ok: false, message: live.reason };
  if (Number(promo.max_redemptions) > 0 && redemptionCount(promo.promo_id) >= Number(promo.max_redemptions)) {
    return { ok: false, message: 'This promo has reached its redemption limit.' };
  }
  if (userAlreadyRedeemed(promo.promo_id, userId)) return { ok: false, message: 'You have already redeemed this promo.' };

  const grantId = await applyPromoEffect(promo, userId, createdBy);
  await addRow('user_promo', {
    promo_id: promo.promo_id, code: promo.promo_code, redeemed_by_user_id: userId,
    status: 'redeemed', redeemed_at: nowStamp(), granted_grant_id: grantId ?? null,
    is_active: 1, is_deleted: 0, created_by: createdBy ?? null,
  }, 'user_promo_id');
  await bumpRedemptions(promo);
  return { ok: true, message: `Redeemed ${promo.promo_code}.`, grantId, promoId: promo.promo_id };
}

async function bumpRedemptions(promo: any): Promise<void> {
  try { await saveRow('site_service_promos', promo.promo_id, { redemptions_used: Number(promo.redemptions_used || 0) + 1 }, 'promo_id'); }
  catch { /* best-effort counter */ }
}

/** Revoke an issued/redeemed ledger row (does not auto-remove a granted entitlement). */
export async function revokeUserPromo(userPromoId: number): Promise<void> {
  await saveRow('user_promo', userPromoId, { status: 'revoked' }, 'user_promo_id');
}
